import React from "react";
import Overlay from "../Overlay";
import SimpleTable from "../tables/SimpleTable";
import { getBoolSelectVal, hideModal, showModalNoOutsideClick } from "../../util/FormatUtil";
import { sweetalert } from "../../App";
import { Column } from "../tables/TableBase";
import { Sorter, TableOrder } from "../../sorting/Sorter";
import AdminAPI from "../../network/AdminAPI";
import FAQAPI from "../../network/FAQAPI";
import { FAQ } from "../../types/FAQ";
import { Category } from "../../types/Category";
import { FAQManagementModal } from "../modals/FAQManagementModal";
import { FAQSortModal } from "../modals/FAQSortModal";

interface FAQManagementState {
  showLoading: boolean;
  selectedFAQ: FAQ;
  tableData: FAQ[];
  categories: Category[];
  direction?: TableOrder;
  isGlobal: boolean;
}

export class FAQManagement extends React.Component<any, FAQManagementState> {

  constructor(props) {
    super(props);
    this.state = {
      showLoading: false,
      selectedFAQ: {} as FAQ,
      tableData: [],
      categories: [],
      direction: "asc",
      isGlobal: false,
    };
    this.createOrModifyFAQ = this.createOrModifyFAQ.bind(this);
    this.loadCategories = this.loadCategories.bind(this);
  }

  componentDidMount() {
    document.title = 'FAQ Management Page';
    AdminAPI.getPermissions().then((data) => {
      this.setState({ isGlobal: data.isGlobal });
    });
    this.loadFAQs();
    this.loadCategories();
  }

  loadCategories() {
    FAQAPI.getCategories().then((response) => {
      if (response.success) {
        this.setState({ categories: response.data || [] });
      }
    }).catch((e) => console.error(e));
  }

  loadFAQs() {
    this.setState({ showLoading: true }, async () => {
      try {
        let response = await FAQAPI.getAllFAQs();
        if (!response.success) {
          this.setState({ showLoading: false, tableData: [] });
          return sweetalert.fire({ icon: "error", title: "", text: "Unable to load FAQs at this time." });
        }
        this.setState({ tableData: response.data || [], showLoading: false });
      } catch (e) {
        console.error(e);
        this.setState({ showLoading: false });
        return sweetalert.fire({ icon: "error", title: "", text: "Unable to load FAQs at this time." });
      }
    });
  }

  createOrModifyFAQ(faq: FAQ) {
    this.setState({ showLoading: true }, async () => {
      try {
        let response = faq.ID === null
          ? await FAQAPI.createFAQ(faq)
          : await FAQAPI.editFAQ(faq);

        if (!response.success) {
          let msg = response.reason ? response.reason : "Unable to save FAQ at this time.";
          this.setState({ showLoading: false });
          return sweetalert.fire({ icon: "error", title: "", text: msg });
        }

        sweetalert.fire({ icon: "success", title: "", text: "FAQ saved" }).then(() => {
          this.setState({ selectedFAQ: {} as FAQ }, () => this.loadFAQs());
        });
      } catch (e) {
        console.error(e);
        this.setState({ showLoading: false });
        return sweetalert.fire({ icon: "error", title: "", text: "Unable to save FAQ at this time." });
      }
    });
  }

  useSorter(col: Column) {
    let sorter = new Sorter<any>();
    this.setState({
      tableData: sorter.sortByKey(this.state.tableData, col.key as keyof any, this.state.direction),
      direction: this.state.direction === "asc" ? "desc" : "asc",
    });
  }

  render() {
    return (
      <React.Fragment>
        <Overlay show_loading={this.state.showLoading} />
        <FAQManagementModal
          selectedFAQ={this.state.selectedFAQ}
          categories={this.state.categories}
          reloadCategories={this.loadCategories}
          onSubmit={(faq) => {
            hideModal(FAQManagementModal.ID);
            this.createOrModifyFAQ(faq);
          }}
        />
        <FAQSortModal
          faqs={this.state.tableData}
          onSaved={() => this.loadFAQs()}
        />

        <div className="container-fluid">
          <div className={"row"}>
            <div className="col-12 col-md-12 col-lg-8 col-xl-5 pt-2">
              <main id="main-content" tabIndex={-1} aria-label="FAQ Management">
                <div className="card mb-2">
                  <div className="card-header verlag-bold">
                    <h3>FAQ Management</h3>
                  </div>
                  <div className="card-footer">
                    {this.state.isGlobal &&
                      <>
                        {this.state.tableData.length > 0 &&
                          <button
                            className="btn immySubmitButtonOutline"
                            onClick={() => showModalNoOutsideClick(FAQSortModal.ID)}
                          >
                            Sort Order
                          </button>
                        }
                        <button
                          className="btn btn-outline-success float-right"
                          onClick={() =>
                            this.setState(
                              { selectedFAQ: { ID: null, Question: "", Answer: "", CategoryID: null, IsActive: 1 } },
                              () => showModalNoOutsideClick(FAQManagementModal.ID)
                            )
                          }
                        >
                          Create New
                        </button>
                      </>
                    }
                  </div>
                </div>
              </main>
            </div>
            {this.state.tableData && this.state.tableData.length > 0 && (
              <div className="col-12 col-md-12 col-lg-12 pt-2">
                <div className="card mt-2" style={{ marginBottom: '4rem' }}>
                  <div className="card-header verlag-bold stickToTop">
                    <h4 className="text-center text-md-left">
                      FAQs
                      <section className="tableHeaderSection float-md-right d-flex justify-content-around">
                        <h4 className={"float-md-right"} aria-label="Total Records" role="alert">
                          Total: {this.state.tableData.length}
                        </h4>
                      </section>
                    </h4>
                  </div>
                  <div className="card-body p-0 m-0 table-responsive">
                    <SimpleTable
                      columns={[
                        {
                          label: "Question",
                          key: "Question",
                          popoverText: "Click to Sort By Question",
                          rawFormat: (val) => {
                            if (!this.state.isGlobal) {
                              return <p>{val.Question}</p>;
                            }
                            return (
                              <a
                                href={"#"}
                                className={'tableNameLinkColor'}
                                onClick={() => {
                                  this.setState({ selectedFAQ: val }, () =>
                                    showModalNoOutsideClick(FAQManagementModal.ID)
                                  );
                                }}
                              >
                                {val.Question}
                              </a>
                            );
                          },
                        },
                        {
                          label: "Category",
                          key: "CategoryName",
                          popoverText: "Click to Sort By Category",
                          rawFormat: (val) => <p>{val.CategoryName ? val.CategoryName : 'General'}</p>,
                        },
                        {
                          label: "Display Order",
                          key: "DisplayOrder",
                          popoverText: "Click to Sort By Display Order",
                        },
                        {
                          label: "Is Active?",
                          key: "IsActive",
                          popoverText: "Click to Sort By Is Active",
                          rawFormat: (val) => {
                            let string = getBoolSelectVal(val.IsActive);
                            return <p>{string.label}</p>;
                          },
                        },
                      ]}
                      table_data={this.state.tableData}
                      columnClickedCallback={(col) => {
                        this.useSorter(col);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}
